import axios from 'axios';
import type { 
  User, 
  Session, 
  Project, 
  Version, 
  Task,
  VehicleNodes,
  AuthResponse,
  FunctionalTask
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
client.interceptors.request.use((config) => {
  // Try to get the latest session from localStorage
  const sessionStr = localStorage.getItem('session');
  if (sessionStr) {
    try {
      const session = JSON.parse(sessionStr);
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (e) {
      console.error('Failed to parse session:', e);
    }
  }
  return config;
});

// Response interceptor to handle 401 errors and auto-refresh token
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 error and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Get refresh token from session
        const sessionStr = localStorage.getItem('session');
        if (sessionStr) {
          const session = JSON.parse(sessionStr);
          if (session?.refresh_token) {
            // Call refresh API
            const { data } = await client.post('/auth', {
              action: 'refresh',
              refresh_token: session.refresh_token,
            });
            
            if (data.session) {
              // Update session with new tokens
              const newSession = {
                ...session,
                ...data.session,
                login_time: session.login_time || Date.now(),
              };
              localStorage.setItem('session', JSON.stringify(newSession));
              
              // Update auth-storage as well
              const authStorageStr = localStorage.getItem('auth-storage');
              if (authStorageStr) {
                const authStorage = JSON.parse(authStorageStr);
                authStorage.state.session = newSession;
                localStorage.setItem('auth-storage', JSON.stringify(authStorage));
              }
              
              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${data.session.access_token}`;
              return client(originalRequest);
            }
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, redirect to login or show message
        localStorage.removeItem('session');
        window.dispatchEvent(new CustomEvent('auth:expired'));
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const { data } = await client.post('/auth', {
      action: 'login',
      email,
      password,
    });
    return data;
  },

  register: async (email: string, password: string): Promise<AuthResponse> => {
    const { data } = await client.post('/auth', {
      action: 'register',
      email,
      password,
    });
    return data;
  },

  logout: async (): Promise<void> => {
    await client.post('/auth', { action: 'logout' });
  },

  verify: async (token?: string): Promise<User> => {
    const { data } = await client.post('/auth', { action: 'verify', token }, {});
    return data.user;
  },

  refreshSession: async (refreshToken: string): Promise<{ session: Session }> => {
    const { data } = await client.post('/auth', { 
      action: 'refresh',
      refresh_token: refreshToken,
    });
    return data;
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const { data } = await client.post('/auth', {
      action: 'forgot_password',
      email,
    });
    return data;
  },
};

// Projects API
export const projectsApi = {
  getAll: async (): Promise<Project[]> => {
    const { data } = await client.get('/projects');
    return data.projects;
  },

  getById: async (id: string): Promise<Project> => {
    const { data } = await client.get(`/projects/${id}`);
    return data.project;
  },

  create: async (project: Omit<Project, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<Project> => {
    const payload = {
      ...project,
      vehicleNodes: project.nodes,
    };
    const { data } = await client.post('/projects', payload);
    return data.project;
  },

  update: async (id: string, project: Partial<Project>): Promise<Project> => {
    const { data } = await client.put('/projects', { id, ...project });
    return data.project;
  },

  delete: async (id: string): Promise<void> => {
    await client.delete(`/projects`, { params: { id } });
  },
};

// Versions API
export const versionsApi = {
  getAll: async (projectId: string): Promise<Version[]> => {
    const { data } = await client.get('/versions', {
      params: { project_id: projectId },
    });
    return data.versions;
  },

  create: async (version: Omit<Version, 'id' | 'created_at'>): Promise<Version> => {
    const { data } = await client.post('/versions', version);
    return data.version;
  },

  delete: async (projectId: string, version: string): Promise<void> => {
    await client.delete('/versions', {
      data: { projectId, version }
    });
  },
};

// Timing Generation API
export const timingApi = {
  generate: async (nodes: VehicleNodes): Promise<Task[]> => {
    const { data } = await client.post('/generate-timing', { projectInfo: nodes });
    return data.tasks;
  },
};

// Risk Analysis API
export const riskApi = {
  analyze: async (projectId: string): Promise<{
    totalTasks: number;
    completedTasks: number;
    delayedTasks: number;
    atRiskTasks: number;
  }> => {
    const { data } = await client.post('/analyze-project', { projectId });
    return data;
  },
};

export interface TaskMatchingRequest {
  functionalTaskType: FunctionalTask;
  functionalTaskDescription: string;
  projectTasks: {
    name: string;
    description: string;
    start: string;
    end: string;
    phase: string;
  }[];
  projectContext: {
    name: string;
    oem: string;
  };
}

export interface TaskMatchingResult {
  functionalTaskType: FunctionalTask;
  matchedTaskName: string | null;
  confidence: number;
  reason: string;
}

export const taskMatchingApi = {
  matchFunctionalTask: async (request: TaskMatchingRequest): Promise<TaskMatchingResult> => {
    const { data } = await client.post('/match-functional-task', request);
    return data;
  },
};

export default client;
